import React, { useState, useRef, useEffect } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { gsap } from 'gsap';
import './UpcomingListWidget.scss';
import { TaskItemWithCaseDetails } from '../TaskItemWithCaseDetails';
import { useNavigate } from 'react-router-dom';
import { formatPartyNames } from '../TruncateParties';
import { getAppealData, getAppealDataByUser } from '../../services/api/DisplayBoardApi';
import JusticeLoader from '../JusticeLoader/JusticeLoader';

import doc from '../../assets/images/note-icon.png'
import court from '../../assets/images/court-icon.png'
import next from '../../assets/images/next-ixon.png'
import adv from '../../assets/images/adv-icon.png'
import cal from '../../assets/images/cal-icon.png'
import bar from '../../assets/images/bar-icon.png'
import task from '../../assets/images/taskhead-icon.png'

const ICONS = {
  DOCUMENT: (
    <img
      src={doc}
      alt="Document"
      className="icon-image"
      width="16"
      height="16"
    />
  ),
  SCALE: (
    <img
      src={bar}
      alt="Scale"
      className="icon-image"
      width="16"
      height="16"
    />
  ),
  COURT: (
    <img
      src={court}
      alt="Court"
      className="icon-image"
      width="16"
      height="16"
    />
  ),
  ADVOCATE: (
    <img
      src={adv}
      alt="Advocate"
      className="icon-image"
      width="16"
      height="16"
    />
  ),
  CALENDAR: (
    <img
      src={cal}
      alt="Calendar"
      className="icon-image"
      width="16"
      height="16"
    />
  ),
  NEXT: (
    <img
      src={next}
      alt="Next"
      className="icon-image"
      width="16"
      height="16"
    />
  ),
  TASK: (
    <img
      src={task}
      alt="Task"
      className="icon-image"
      width="16"
      height="16"
    />
  ),
  HEARING: (
    <img
      src={bar}
      alt="Hearing"
      className="icon-image"
      width="16"
      height="16"
    />
  )
};

const UpcomingListWidget = ({ data, selectedUserId }) => {
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState('hearings');
  const [activeHearingSubTab, setActiveHearingSubTab] = useState('all-cases');
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [showAllHearings, setShowAllHearings] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '', refTask: false });

  const [appealData, setAppealData] = useState({
    reappealable: [],
    reappealed: []
  });
  const [appealDataFetched, setAppealDataFetched] = useState(false);
  const [isLoadingAppealData, setIsLoadingAppealData] = useState(false);
  const [appealTabCountRequested, setAppealTabCountRequested] = useState({
    're-appealable': false,
    're-appealed': false
  });

  const tasksContentRef = useRef(null);
  const hearingsContentRef = useRef(null);

  useEffect(() => {
    if ((activeHearingSubTab === 're-appealable' || activeHearingSubTab === 're-appealed')) {
      setAppealTabCountRequested(prev => ({ ...prev, [activeHearingSubTab]: true }));
      if (!appealDataFetched) {
        fetchAppealData();
      }
    }
  }, [activeHearingSubTab, appealDataFetched]);

  useEffect(() => {
    if (appealDataFetched) {
      setAppealDataFetched(false);
      setAppealData({
        reappealable: [],
        reappealed: []
      });
    }
  }, [selectedUserId]);

  const fetchAppealData = async () => {
    if (activeHearingSubTab === 're-appealable' || activeHearingSubTab === 're-appealed') {
      setIsLoadingAppealData(true);
    }

    try {
      let response;

      if (selectedUserId) {
        response = await getAppealDataByUser(selectedUserId);
      } else {
        response = await getAppealData();
      }

      if (response && response.data) {
        setAppealData(response.data);
        setAppealDataFetched(true);
      }
    } catch (error) {
      console.error('Error fetching appeal data:', error);
      setAppealData({
        reappealable: [],
        reappealed: []
      });
    } finally {
      if (activeHearingSubTab === 're-appealable' || activeHearingSubTab === 're-appealed') {
        setIsLoadingAppealData(false);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      const options = { weekday: 'short', month: 'short', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    }
  };

  const handleMouseEnter = (event, fullParties, isRefTask = false) => {
    if (isRefTask) {
      const containerRect = document.querySelector('.upcoming-widget').getBoundingClientRect();
      setTooltip({
        visible: true,
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top - 40,
        content: fullParties,
        refTask: true
      });
    } else {
      const rect = event.target.getBoundingClientRect();
      const containerRect = document.querySelector('.upcoming-widget').getBoundingClientRect();
      setTooltip({
        visible: true,
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.top - containerRect.top - 40,
        content: fullParties,
        refTask: false
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, content: '', refTask: false });
  };

  const sortHearingsByCourtType = (hearings) => {
    return hearings.sort((a, b) => {
      const courtTypePriority = {
        'high-court': 1,
        'district-court': 2,
        'supreme-court': 3,
      };

      const aPriority = courtTypePriority[a.type] || 999;
      const bPriority = courtTypePriority[b.type] || 999;

      return aPriority - bPriority;
    });
  };

  const filterHearingsBySubTab = (hearings, subTab) => {
    if (!hearings || !hearings.length) return [];

    switch (subTab) {
      case 'all-cases':
        // Filter cases that have a next hearing date today or in the future
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

        return hearings.filter(hearing => {
          // Check if next hearing date exists in customUndatedRows or caseStatus
          const nextHearingDate = hearing.caseDate?.customUndatedRows?.["Next Hearing Date"] ||
            hearing.caseDate?.caseStatus?.["Next Hearing Date"];

          // If no next hearing date exists, exclude the case
          if (!nextHearingDate) return false;

          // Use dateStr field which is already in YYYY-MM-DD format for comparison
          const dateStr = hearing.dateStr;

          if (!dateStr) return false; // Exclude cases without a date

          // Parse the date string
          const hearingDate = new Date(dateStr);
          hearingDate.setHours(0, 0, 0, 0); // Reset time for accurate comparison

          // Include only if the hearing date is today or in the future
          return hearingDate >= today;
        });

      case 're-appealable':
        if (appealData.reappealable && appealData.reappealable.length > 0) {
          return appealData.reappealable.filter(item => item.status?.isReAppealable === true);
        }
        return hearings.filter(hearing =>
          hearing.caseDate?.status?.isReAppealable === true
        );

      case 're-appealed':
        if (appealData.reappealed && appealData.reappealed.length > 0) {
          return appealData.reappealed.filter(item => item.status?.reAppealed === true);
        }
        return hearings.filter(hearing =>
          hearing.caseDate?.status?.reAppealed === true
        );

      default:
        return hearings;
    }
  };

  const getHearingSubTabCount = (subTab) => {
    switch (subTab) {
      case 'all-cases':
        if (!data || Object.keys(data).length === 0) return 0;
        let totalCount = 0;
        sortedDates.forEach(date => {
          const hearings = data[date]?.hearings;
          if (hearings && hearings.length > 0) {
            totalCount += hearings.length;
          }
        });
        return totalCount;

      case 're-appealable':
        if (!appealTabCountRequested['re-appealable']) return '';
        if (appealData.reappealable && appealData.reappealable.length > 0) {
          return appealData.reappealable.filter(item => item.status?.isReAppealable === true).length;
        }
        if (!data || Object.keys(data).length === 0) return 0;
        let reAppealableCount = 0;
        sortedDates.forEach(date => {
          const hearings = data[date]?.hearings;
          if (hearings && hearings.length > 0) {
            const filteredHearings = hearings.filter(hearing =>
              hearing.caseDate?.status?.isReAppealable === true
            );
            reAppealableCount += filteredHearings.length;
          }
        });
        return reAppealableCount;

      case 're-appealed':
        if (!appealTabCountRequested['re-appealed']) return '';
        if (appealData.reappealed && appealData.reappealed.length > 0) {
          return appealData.reappealed.filter(item => item.status?.reAppealed === true).length;
        }
        if (!data || Object.keys(data).length === 0) return 0;
        let reAppealedCount = 0;
        sortedDates.forEach(date => {
          const hearings = data[date]?.hearings;
          if (hearings && hearings.length > 0) {
            const filteredHearings = hearings.filter(hearing =>
              hearing.caseDate?.status?.reAppealed === true
            );
            reAppealedCount += filteredHearings.length;
          }
        });
        return reAppealedCount;

      default:
        return 0;
    }
  };

  const sortedDates = data && Object.keys(data).length > 0 ? Object.keys(data).sort((a, b) => new Date(a) - new Date(b)) : [];

  const getFilteredAndSortedDates = (tab) => {
    if (tab === 'hearings' && (activeHearingSubTab === 're-appealable' || activeHearingSubTab === 're-appealed')) {
      return ['appeal-data'];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return sortedDates.filter(date => {
      if (tab === 'tasks') {
        const items = data[date]?.tasks;
        if (!items || items.length === 0) return false;

        // Filter tasks to only include those with due date today or in the future and not completed
        const filteredTasks = items.filter(task => {
          if (!task.dueDate || task.isCompleted) return false;
          const dueDate = new Date(task.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate >= today;
        });

        return filteredTasks.length > 0;
      } else if (tab === 'hearings') {
        const items = data[date]?.hearings;
        if (!items || items.length === 0) return false;

        const filteredItems = filterHearingsBySubTab(items, activeHearingSubTab);
        return filteredItems.length > 0;
      }
      return false;
    });
  };

  const currentVisibleDates = (tab) => {
    const isShowingAll = tab === 'tasks' ? showAllTasks : showAllHearings;
    const filteredDates = getFilteredAndSortedDates(tab);
    return isShowingAll ? filteredDates : filteredDates.slice(0, 3);
  };

  const hasMoreDates = (tab) => {
    return getFilteredAndSortedDates(tab).length > 3;
  };

  const toggleShowAll = (tab) => {
    if (tab === 'tasks') {
      setShowAllTasks(!showAllTasks);
    } else {
      setShowAllHearings(!showAllHearings);
    }
  };

  useEffect(() => {
    const currentRef = activeTab === 'tasks' ? tasksContentRef : hearingsContentRef;
    const isShowingAll = activeTab === 'tasks' ? showAllTasks : showAllHearings;

    if (currentRef.current) {
      if (isShowingAll) {
        gsap.set(currentRef.current, { height: 'auto' });
      } else {
        const initialHeight = calculateInitialHeight();
        gsap.to(currentRef.current, {
          duration: 0.3,
          height: initialHeight,
          ease: 'power2.out',
          overwrite: 'auto'
        });
      }
    }
  }, [activeTab, showAllTasks, showAllHearings]);

  const calculateInitialHeight = () => {
    return 'auto';
  };

  const currentTabShowAll = activeTab === 'tasks' ? showAllTasks : showAllHearings;
  const currentTabHasMore = hasMoreDates(activeTab);

  const getItemCount = (tab) => {
    if (tab === 'hearings' && (activeHearingSubTab === 're-appealable' || activeHearingSubTab === 're-appealed')) {
      return getHearingSubTabCount(activeHearingSubTab);
    }

    if (!data || Object.keys(data).length === 0) return 0;

    return getFilteredAndSortedDates(tab).reduce((total, date) => {
      if (tab === 'tasks') {
        const items = data[date]?.[tab];
        return total + (items ? items.length : 0);
      } else if (tab === 'hearings') {
        const items = data[date]?.hearings;
        if (!items) return total;

        const filteredItems = filterHearingsBySubTab(items, activeHearingSubTab);
        return total + filteredItems.length;
      }
      return total;
    }, 0);
  };

  const renderAppealItems = (appealItems, type) => {
    if (!appealItems || appealItems.length === 0) {
      return (
        <div className="no-data">
          No {type} cases found
        </div>
      );
    }

    return (
      <div className="date-section">
        <div className="date-header">
          {type === 're-appealable' ? 'Re-Appealable Cases' : 'Re-Appealed Cases'}
          <span className="item-count">
            {appealItems.length} case{appealItems.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="items-grid">
          {appealItems.map((item, index) => (
            <div
              key={item.courtRef || index}
              className={`upcoming-item-card hearing-card hearing-card--${item.courtType || 'high-court'}`}
              onClick={() => {
                navigate(`/home/cases/casedetails/${item.courtRef}`);
              }}
            >
              <span className="card-emoji">
                <span className={`court-type-badge ${item.courtType || 'high-court'}`}>
                  {(item.courtType || 'high-court') === 'high-court' ? 'HC' : (item.courtType === 'supreme-court' ? 'SC' : 'DC')}
                </span>
              </span>
              <div className="card-details">
                <div className="card-title">
                  <span
                    className="hearing-parties tooltip-trigger"
                    onMouseEnter={(e) => handleMouseEnter(e, item.parties, false)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {formatPartyNames(item.parties)}
                  </span>
                </div>
                {item.caseTypeAndNumber && (
                  <div className="card-sub">{ICONS.DOCUMENT} {item.caseTypeAndNumber}</div>
                )}
                {item.caseStatus?.['Case Status'] && (
                  <div className="card-sub">{ICONS.SCALE} Status: {item.caseStatus['Case Status']}</div>
                )}
                {item.caseStatus?.Coram && (
                  <div className="card-sub">{ICONS.COURT} Judge: {item.caseStatus.Coram}</div>
                )}
                {item.advocates && (
                  <div className="card-sub">{ICONS.ADVOCATE} Advocate: {item.advocates}</div>
                )}
                {item.caseDetails?.['Filing Date'] && (
                  <div className="card-sub">{ICONS.CALENDAR} Filed: {item.caseDetails['Filing Date']}</div>
                )}
                {type === 're-appealed' && item.reAppealInfo?.appealDeadlineDate && (
                  <div className="card-sub">{ICONS.NEXT} Appeal Deadline: {item.reAppealInfo.appealDeadlineDate}</div>
                )}

                {item.status?.assignedData && item.status.assignedData.length > 0 && (
                  <div className="card-sub">
                    {ICONS.ADVOCATE} Assigned:
                    {item.status.assignedData.map((assigned, index) => (
                      <span
                        key={assigned.id}
                        style={{ cursor: 'pointer', fontWeight: 'bold', color: '#004A9A', marginLeft: '4px' }}
                        onClick={() => (window.location.href = `/home/addusers/${assigned.id}`)}
                      >
                        {assigned.name}
                        {index < item.status.assignedData.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = (tab) => {
    if (isLoadingAppealData && tab === 'hearings' && (activeHearingSubTab === 're-appealable' || activeHearingSubTab === 're-appealed')) {
      return <JusticeLoader />;
    }

    if (tab === 'hearings' && (activeHearingSubTab === 're-appealable' || activeHearingSubTab === 're-appealed')) {
      if (activeHearingSubTab === 're-appealable') {
        const filteredItems = appealData.reappealable?.filter(item => item.status?.isReAppealable === true) || [];
        return renderAppealItems(filteredItems, 're-appealable');
      } else if (activeHearingSubTab === 're-appealed') {
        const filteredItems = appealData.reappealed?.filter(item => item.status?.reAppealed === true) || [];
        return renderAppealItems(filteredItems, 're-appealed');
      }
    }

    if (!data || Object.keys(data).length === 0) {
      return (
        <div className="no-data">
          No data available
        </div>
      );
    }

    const datesToDisplay = currentVisibleDates(tab);
    const hasAnyContent = getFilteredAndSortedDates(tab).length > 0;

    if (!hasAnyContent) {
      return (
        <div className="no-data">
          No upcoming {tab} scheduled
        </div>
      );
    }

    return (
      <>
        {datesToDisplay.map((date) => {
          let items = data[date]?.[tab];
          if (!items?.length) return null;

          if (tab === 'hearings') {
            items = filterHearingsBySubTab(items, activeHearingSubTab);
            if (!items.length) return null;
            items = sortHearingsByCourtType([...items]);
          } else if (tab === 'tasks') {
            // Filter tasks to show only those with due date today or in the future and not completed
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            items = items.filter(task => {
              if (!task.dueDate || task.isCompleted) return false; // Exclude tasks without due date or completed tasks

              const dueDate = new Date(task.dueDate);
              dueDate.setHours(0, 0, 0, 0);

              return dueDate >= today; // Include only if due date is today or in the future
            });

            if (!items.length) return null;
          }

          return (
            <div className="date-section" key={date}>
              <div className="date-header">
                {formatDate(date)}
                <span className="item-count">
                  {items.length} {tab === 'tasks' ? 'task' : 'hearing'}{items.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="items-grid">
                {items.map((item, index) => (
                  <div
                    key={item._id || index}
                    className={`upcoming-item-card ${tab === 'tasks' ? 'upcoming-task-card' : 'hearing-card'} ${tab === 'hearings' ? `hearing-card--${item.type}` : ''}`}
                    onClick={() => {
                      if (tab === 'tasks') {
                        navigate(`/home/tasks/viewtask/${item._id}`);
                      } else if (tab === 'hearings') {
                        navigate(`/home/cases/casedetails/${item.courtRef}`);
                      }
                    }}
                  >
                    {tab === 'tasks' ? (
                      <TaskItemWithCaseDetails
                        item={item}
                        formatDate={formatDate}
                        handleTooltipEnter={(e, parties) => handleMouseEnter(e, parties, true)}
                        handleTooltipLeave={handleMouseLeave}
                      />
                    ) : (
                      <>
                        <span className="card-emoji">
                          <span className={`court-type-badge ${item.type}`}>
                            {item.type === 'high-court' ? 'HC' : item.type === 'supreme-court' ? 'SC' : 'DC'}
                          </span>
                        </span>
                        <div className="card-details">
                          <div className="card-title">
                            <span
                              className="hearing-parties tooltip-trigger"
                              onMouseEnter={(e) => handleMouseEnter(e, item.caseDate.parties, false)}
                              onMouseLeave={handleMouseLeave}
                            >
                              {formatPartyNames(item.caseDate.parties)}
                            </span>
                            <span className='courtref-displayboard'></span>
                          </div>
                          {item.caseDate?.caseTypeAndNumber && (
                            <div className="card-sub">{ICONS.DOCUMENT} {item.caseDate.caseTypeAndNumber}</div>
                          )}
                          {item.caseDate?.caseStatus?.['Case Stage'] || item.caseDate?.caseStatus?.["Stage of Case"] || item.caseDate?.caseStatus?.['Status/Stage'] ? (
                            <div className="card-sub">{ICONS.SCALE} Stage: {item.caseDate.caseStatus['Case Stage'] || item.caseDate.caseStatus["Stage of Case"] || item.caseDate.caseStatus['Status/Stage']}</div>
                          ) : null}
                          {item.caseDate?.caseStatus?.['Court Number and Judge'] || item.caseDate?.caseStatus?.['Coram'] ? (
                            <div className="card-sub">{ICONS.COURT} Judge: {item.caseDate.caseStatus['Court Number and Judge'] || item.caseDate.caseStatus['Coram']}</div>
                          ) : null}
                          {item.caseDate?.advocates && (
                            <div className="card-sub">{ICONS.ADVOCATE} Advocate: {item.caseDate.advocates}</div>
                          )}
                          {item.caseDate?.caseDetails?.['Filing Date'] && (
                            <div className="card-sub">{ICONS.CALENDAR} Filed: {item.caseDate.caseDetails['Filing Date']}</div>
                          )}
                          {item.nextHearingDate && (
                            <div className="card-sub">{ICONS.NEXT} Next: <span className={item.caseDate?.customUndatedRows?.["Next Hearing Date"] ? "custom-next-date" : ""}>{item.caseDate?.customUndatedRows?.["Next Hearing Date"] || item.caseDate?.caseStatus?.['Next Hearing Date']}</span></div>
                          )}

                          {item.caseDate?.status?.assignedData && item.caseDate.status.assignedData.length > 0 && (
                            <div className="card-sub">
                              {ICONS.ADVOCATE} Assigned:
                              {item.caseDate.status.assignedData.map((assigned, index) => (
                                <span
                                  key={assigned.id}
                                  style={{ cursor: 'pointer', fontWeight: 'bold', color: '#004A9A', marginLeft: '4px' }}
                                  onClick={() => (window.location.href = `/home/addusers/${assigned.id}`)}
                                >
                                  {assigned.name}
                                  {index < item.caseDate.status.assignedData.length - 1 ? ',' : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="upcoming-widget" style={{ position: 'relative' }}>
      {/* Combined tabs and sub-tabs container */}
      <div className="tabs-container">
        <div className="tabs">
          {['hearings', 'tasks'].map((tab) => (
            <button
              key={tab}
              className={activeTab === tab ? 'active' : ''}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'hearings' ? 'Hearings' : 'Tasks'}
              <span className="tab-count">
                {getItemCount(tab)}
              </span>
            </button>
          ))}
        </div>

        {/* Hearing Sub-tabs - Now in the same row */}
        {activeTab === 'hearings' && (
          <div className="hearing-sub-tabs">
            {[
              { key: 'all-cases', label: 'Pending Cases' },
              { key: 're-appealable', label: 'Re-Appealable' },
              { key: 're-appealed', label: 'Re-Appealed' }
            ].map((subTab) => (
              <button
                key={subTab.key}
                className={activeHearingSubTab === subTab.key ? 'active' : ''}
                onClick={() => setActiveHearingSubTab(subTab.key)}
              >
                {subTab.label}
                {getHearingSubTabCount(subTab.key) !== '' && (
                  <span className="tab-count">
                    {getHearingSubTabCount(subTab.key)}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={`upcoming-content-wrapper ${!currentTabShowAll && currentTabHasMore ? 'limited' : ''}`}>
        <div className="upcoming-content-area"
          ref={activeTab === 'tasks' ? tasksContentRef : hearingsContentRef}>
          {renderContent(activeTab)}
        </div>

        {currentTabHasMore && (
          <div
            className={`show-more-toggle-container ${currentTabShowAll ? 'expanded' : ''}`}
            onClick={() => toggleShowAll(activeTab)}
          >
            <span className="toggle-text">
              {currentTabShowAll ? 'Show less' : 'Show more'}
            </span>
            <div className="toggle-circle">
              <IoIosArrowDown className="arrow-icon" />
            </div>
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          className={`custom-tooltip${tooltip.refTask ? ' tooltip-ref-task' : ''}`}
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.content}
        </div>
      )}

    </div>
  );
};

export default UpcomingListWidget;